import { ref, readonly, type Ref, type DeepReadonly } from "vue";
import { captureException } from "@sentry/vue";
import { adminV1OrganizationsPaymentsScheduledPaymentsRetrieve } from "../api";
import type { ScheduledPaymentDetail } from "@/api/model";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";

interface UseScheduledPaymentDetailReturn {
  detail: DeepReadonly<Ref<ScheduledPaymentDetail | null>>;
  isLoading: DeepReadonly<Ref<boolean>>;
  error: DeepReadonly<Ref<string | null>>;
  fetchDetail: (scheduledPaymentId: string) => Promise<void>;
}

export function useScheduledPaymentDetail(): UseScheduledPaymentDetailReturn {
  const { currentOrganizationId } = useCurrentOrganization();
  const detail = ref<ScheduledPaymentDetail | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  let latestRequestId = 0;

  const _handleError = (err: unknown): void => {
    captureException(err, {
      extra: { message: "Failed to load scheduled payment detail" },
    });
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 401 || status === 403) {
      error.value = "You don't have permission to view this payment";
      return;
    }
    if (status === 404) {
      error.value = "Scheduled payment not found";
      return;
    }
    error.value = "Failed to load payment detail";
  };

  const fetchDetail = async (scheduledPaymentId: string): Promise<void> => {
    if (!currentOrganizationId.value) {
      isLoading.value = false;
      detail.value = null;
      error.value = "Organization not found";
      return;
    }
    const requestId = ++latestRequestId;
    isLoading.value = true;
    error.value = null;
    detail.value = null;

    try {
      const result =
        await adminV1OrganizationsPaymentsScheduledPaymentsRetrieve(
          currentOrganizationId.value,
          scheduledPaymentId
        );
      if (requestId !== latestRequestId) return;
      detail.value = result;
    } catch (err: unknown) {
      if (requestId !== latestRequestId) return;
      _handleError(err);
    } finally {
      if (requestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  };

  return {
    detail: readonly(detail),
    isLoading: readonly(isLoading),
    error: readonly(error),
    fetchDetail,
  };
}
